import userPreferences from "../../../services/user-preferences/userPreferences";
import { USER_SCOPE, ganttViewPrefKey } from "../../../services/user-preferences/userPreferencesApi";

/**
 * The ONLY module that touches the Scenario Gantt's stored view settings
 * (BND-C1). Everything else reads through `readGanttSettings` /
 * `isFeatureEnabled` and writes through `writeGanttSetting` /
 * `writeFeatureEnabled`; the legacy helpers in utils.js delegate here.
 *
 * Stored on the server (SPEC-0061): document user / gantt_view:scenario, so the
 * settings survive logout, another browser and cleared site data. The
 * pre-release localStorage blob (ganttScenarioFilters-V6) is imported once by
 * localPreferencesImport and never read here.
 *
 * Stored shape (BND-C7, version 2):
 *   { version: 2, features: { [FeatureKey]: boolean }, rowHeight?, barMargin?,
 *     taskBorderRadius?, dependencyRadius?, gridCollapse?, ...anything legacy }
 * A version-1 blob is the untagged `{ features, ...numbers }` and is migrated
 * in place on first read — every stored value is kept (no user loses a setting).
 */
export const SETTINGS_VERSION = 2;
export const GANTT_VIEW_ID = "scenario";
const PREF_KEY = ganttViewPrefKey(GANTT_VIEW_ID);

/** Every feature the Scenario Gantt's Settings menu can toggle — stored positively. */
export const FEATURE_KEYS = Object.freeze([
  "criticalPaths",
  "summary",
  "rollups",
  "dependencies",
  "projectLines",
  "cellEdit",
  "baselines",
  "progressLine",
  "parentArea",
  "indicators",
  "colors",
  "showActivityBoundaries",
  "showTimeline",
  "showAdvancedTimeline",
  "showScheduler",
]);

/** Every non-feature setting the menu saves — and applyGanttViewState restores. */
export const SETTING_KEYS = Object.freeze([
  "rowHeight",
  "barMargin",
  "taskBorderRadius",
  "dependencyRadius",
  "animationDuration",
  "gridCollapse",
]);

export const DEFAULT_SETTINGS = Object.freeze({ version: SETTINGS_VERSION, features: {} });

/**
 * What a feature shows for a planner who never toggled it. Only a missing key falls
 * back here, so a stored choice (on or off) always wins over a later default change.
 */
export const FEATURE_DEFAULTS = Object.freeze({
  dependencies: true,
  parentArea: true,
  indicators: true,
  colors: true,
});

export const featureDefault = (key) => Boolean(FEATURE_DEFAULTS[key]);

const listeners = new Set();
let restoreFailureReported = false;
let migrationCount = 0;

const defaults = () => ({ version: SETTINGS_VERSION, features: {} });

/**
 * Reads the stored document defensively: a document that is not an object
 * (hand-edited through the API, an older client) yields the defaults and is
 * reported once instead of throwing and taking the Gantt down.
 */
const parseStored = (stored) => {
  if (stored == null) return { settings: defaults(), stored: false };
  if (typeof stored !== "object" || Array.isArray(stored)) {
    return { settings: defaults(), stored: false, corrupt: true };
  }
  const settings = migrate(stored);
  return { settings, stored: true, migrated: settings.version !== stored.version };
};

/**
 * v1 → v2: add the version, normalise `features` to an object; every other key is
 * carried as-is (BND-C7). Idempotent — a v2 blob comes back unchanged. A blob from
 * a FUTURE version is read as-is too (its keys are a superset), never reset.
 */
export const migrate = (blob) => {
  const features =
    blob.features && typeof blob.features === "object" && !Array.isArray(blob.features)
      ? { ...blob.features }
      : {};
  if (blob.version === SETTINGS_VERSION) return { ...blob, features };
  if (typeof blob.version === "number" && blob.version > SETTINGS_VERSION) {
    return { ...blob, features };
  }
  migrationCount += 1;
  return { ...blob, features, version: SETTINGS_VERSION };
};

/** How many v1 blobs this session migrated (0 once every browser is on v2). */
export const getMigrationCount = () => migrationCount;

const reportRestoreFailure = () => {
  if (restoreFailureReported) return;
  restoreFailureReported = true;
  // eslint-disable-next-line no-console
  console.warn(
    "[gantt-settings] stored view settings could not be read and were reset to defaults"
  );
};

export const readGanttSettings = () => {
  const { settings, corrupt, migrated } = parseStored(
    userPreferences.get(USER_SCOPE, PREF_KEY, null)
  );
  if (corrupt) reportRestoreFailure();
  if (migrated || corrupt) {
    // Persist the migrated shape (or the defaults that replaced an unreadable
    // document) so the next read is clean (BND-C7).
    userPreferences.set(USER_SCOPE, PREF_KEY, settings);
  }
  return settings;
};

/** True when the stored blob was unreadable (for the one-time user notice). */
export const isRestoreFailureReported = () => restoreFailureReported;

let restoreFailureNoticeTaken = false;
/** True exactly once per session after a failed restore — the caller shows the notice. */
export const takeRestoreFailureNotice = () => {
  if (!restoreFailureReported || restoreFailureNoticeTaken) return false;
  restoreFailureNoticeTaken = true;
  return true;
};

export const isFeatureEnabled = (key, defaultValue = featureDefault(key)) => {
  const { features } = readGanttSettings();
  return key in features ? Boolean(features[key]) : defaultValue;
};

const persist = (settings) => {
  userPreferences.set(USER_SCOPE, PREF_KEY, settings);
  return settings;
};

export const writeGanttSetting = (key, value) =>
  persist({ ...readGanttSettings(), [key]: value });

export const writeFeatureEnabled = (key, enabled) => {
  const current = readGanttSettings();
  return persist({ ...current, features: { ...current.features, [key]: Boolean(enabled) } });
};

const concernsSettings = (event) =>
  event.type === "load"
    ? event.scopes.includes(USER_SCOPE)
    : event.scope === USER_SCOPE && event.prefKey === PREF_KEY;

userPreferences.subscribe((event) => {
  if (!concernsSettings(event)) return;
  const settings = readGanttSettings();
  listeners.forEach((listener) => {
    try {
      listener(settings);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[gantt-settings] listener failed", e);
    }
  });
});

/**
 * Notifies on every write made through this module and when the server
 * document lands or changes (login load, one-time import). Returns the
 * unsubscribe function.
 */
export const subscribeGanttSettings = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Test hook: forgets the one-time report and the migration counter. */
export const __resetViewSettingsForTests = () => {
  restoreFailureReported = false;
  migrationCount = 0;
  restoreFailureNoticeTaken = false;
  listeners.clear();
};
